import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import format from "date-fns/format";
import jaLocale from "date-fns/locale/ja";
import parse from "date-fns/parse";
import AddIcon from "@material-ui/icons/Add";
import MonthNavigator from "../common/MonthNavigator";
import { currentYM } from "../../util/DateUtil";
import SelectOrgFilter from "../filter/SelectOrgFilter";
import { LocalStorageKey } from "../../models/LocalStorage";
import {
  allCompanyOption,
  departmentOption,
  OrgOption,
} from "../filter/CommonBumonFilter";
import {
  loadSelectedOptionByCompany,
  orgOptionsToParam,
} from "../../util/OrgUtil";
import { Invoice, InvoiceModel } from "../../models/InvoiceModel";
import {
  LocalStorageKeys,
  LocalStorageService,
} from "../../services/LocalStorageService.";
import { DailyReportInvoicesContext } from "../../screens/dailyReport/DailyReportDetailScreen";
import { DailyReport } from "../../models/DailyReport/DailyReportModel";
import { formatLocalNumber } from "../../util/Math";
import DailyReportInvoiceList from "../common/DailyReport/DailyReportInvoiceList";

interface DailyReportSelectInvoiceTableProps {
  dailyReport: DailyReport | null;
}

const DailyReportSelectInvoiceTable = (
  props: DailyReportSelectInvoiceTableProps
) => {
  const { dailyReport } = props;
  const selectedMyOrganisations = loadSelectedOptionByCompany(
    LocalStorageKey.DAILY_REPORT
  );

  console.log("dailyReport", selectedMyOrganisations);

  const loadSortType =
    LocalStorageService.get(LocalStorageKeys.SORT.Daily_Report) === true;

  const { dailyReportInvoices, setDailyReportInvoices } = useContext(
    DailyReportInvoicesContext
  );

  /**請求一覧 Api読み込み中*/
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({
    [currentYM()]: true,
  });

  const [selectedYM, setSelectedYM] = useState(currentYM());

  /**一覧関連******************************************* */
  const [invoices, setInvoices] = useState<{ [key: string]: Invoice[] }>({
    [currentYM()]: [],
  });
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState<{ [key: string]: number }>({
    [currentYM()]: 0,
  });
  const [last_page, setLastPage] = useState<{ [key: string]: number }>({
    [currentYM()]: -1,
  });

  const [isAsc, setIsAsc] = useState(loadSortType);
  const [fromOrgOptions, setFromOrgOptions] = useState<OrgOption[]>(
    selectedMyOrganisations.fromOrgOptions ?? [departmentOption]
  );
  const [toOrgOptions, setToOrgOptions] = useState<OrgOption[]>(
    selectedMyOrganisations.toOrgOptions ?? [allCompanyOption]
  );

  const observerRef = useRef<HTMLDivElement | null>(null);
  const [isShowCoverIndicator, setIsShowCoverIndicator] =
    useState<boolean>(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const isMounted = useRef(true); // コンポーネントがマウントされているかを追跡

  useEffect(() => {
    isMounted.current = true; // コンポーネントがマウントされた状態
    return () => {
      isMounted.current = false; // アンマウント時に false
    };
  }, []);

  const loadNext = async (
    billing_ym: string = selectedYM,
    isInit: boolean = false
  ) => {
    setIsLoading((pre) => ({ ...pre, [billing_ym]: true }));
    setIsFetching(true); // ボトムローディング中を示すフラグ
    let current_page = isInit ? 0 : page[billing_ym] ?? 0;

    if (current_page === last_page[billing_ym]) {
      setIsFetching(false);
      return;
    }

    const fromOrgParams = orgOptionsToParam(fromOrgOptions);
    const toOrgParams = orgOptionsToParam(toOrgOptions);

    try {
      const res = await InvoiceModel.fetchInvoiceList({
        page: current_page + 1,
        billing_ym: billing_ym,
        from_filter_types: fromOrgParams.filter_types,
        from_org_ids: fromOrgParams.org_ids,
        to_filter_types: toOrgParams.filter_types,
        to_org_ids: toOrgParams.org_ids,
        isAsc: isAsc,
      });

      if (!isMounted.current) return; // アンマウント後に状態更新しない

      setInvoices((pre) => ({
        ...pre,
        [billing_ym]: isInit
          ? res.data
          : (pre[billing_ym] || []).concat(res.data), // ここで undefined 対策
      }));

      setPage((pre) => ({ ...pre, [billing_ym]: res.current_page }));
      setLastPage((pre) => ({ ...pre, [billing_ym]: res.last_page }));
      setIsLoading((pre) => ({ ...pre, [billing_ym]: false }));
    } catch (error) {
      console.error(error);
      // if (isMounted.current) {
      //   window.functions.logout();
      // }
    } finally {
      setIsFetching(false); // ボトムローディングが完了
    }
  };

  const onReload = async (ym?: string) => {
    setIsShowCoverIndicator(true);
    setInvoices((pre) => ({ ...pre, [selectedYM]: [] }));
    setPage((pre) => ({ ...pre, [selectedYM]: 0 }));
    setLastPage((pre) => ({ ...pre, [selectedYM]: -1 }));

    await loadNext(selectedYM, true);
    if (isMounted.current) {
      setIsShowCoverIndicator(false);
      setIsFirstLoad(false);
    }
  };

  const onSelected = (invoice: Invoice) => {
    // 重複チェックを行い、新しい請求書を追加
    if (
      !dailyReportInvoices.some(
        (existingInvoice) => existingInvoice.id === invoice.id
      )
    ) {
      setDailyReportInvoices([...dailyReportInvoices, invoice]);
    }

    // 選択された請求書を既に選択された請求書リストから削除
    invoices[selectedYM] = invoices[selectedYM].filter(
      (existingInvoice) => existingInvoice.id !== invoice.id
    );

    setInvoices({ ...invoices });
  };

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && !isLoading[selectedYM] && !isFetching) {
        loadNext();
      }
    },
    [isLoading, isFetching, selectedYM]
  );

  useEffect(() => {
    // オプション設定: ビューポートのルートをデフォルトにし、マージンを20px、しきい値を1.0に設定
    const option = {
      root: null,
      rootMargin: "20px",
      threshold: 1.0,
    };

    // IntersectionObserverを作成し、指定したオプションとコールバック関数を設定
    const observer = new IntersectionObserver(handleObserver, option);

    // observerRefに現在の要素がある場合、それを監視する
    if (observerRef.current) observer.observe(observerRef.current);

    // クリーンアップ関数: コンポーネントがアンマウントされるときに監視を解除
    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [handleObserver]);

  useEffect(() => {
    onReload();
  }, [fromOrgOptions, toOrgOptions]);

  useEffect(() => {
    onReload();
  }, []);

  return (
    <Box p={2}>
      <FormControl fullWidth variant="outlined" margin="normal">
        {/* <InputLabel>請求元</InputLabel> */}
        <MonthNavigator value={selectedYM} onChange={(v) => setSelectedYM(v)} />

        <SelectOrgFilter
          localStorageKey={LocalStorageKey.DAILY_REPORT}
          defaultFrom={fromOrgOptions}
          onChangeFrom={setFromOrgOptions}
          defaultTo={toOrgOptions}
          onChangeTo={setToOrgOptions}
        />
      </FormControl>

      {/* <Box display="flex" alignItems="center" mb={2}>
        <Switch
          checked={showOwnInvoices}
          onChange={handleSwitchChange}
          color="primary"
        />
        <Typography>自分が作成した請求のみ表示</Typography>
      </Box> */}

      <DailyReportInvoiceList
        invoices={invoices[selectedYM] || []}
        onAdd={onSelected}
      />
      {/* ボトムローディング中のインジケーター表示 */}
      {isFetching && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}

      {/* スクロール終端の監視用要素 */}
      <div ref={observerRef} style={{ height: "20px" }} />
    </Box>
  );
};

export default DailyReportSelectInvoiceTable;
